import { useState, useEffect } from 'react';
import { usersAPI, storesAPI } from '../lib/api';
import { toast } from 'react-hot-toast';
import {
    Users as UsersIcon,
    Plus,
    Search,
    Mail,
    Shield,
    Store,
    Edit2,
    Trash2,
    X,
    Loader2,
    CheckCircle2,
    XCircle,
    Phone,
    Clock,
} from 'lucide-react';
import Layout from '../components/Layout';

interface UserData {
    _id: string;
    fullName: string;
    email: string;
    phone: string;
    role: string;
    organizationId: any;
    storeIds: any[];
    accountStatus: string;
    createdAt: string;
    verificationStatus?: string;
    verificationDocuments?: any[];
}

export default function Users() {
    const [users, setUsers] = useState<UserData[]>([]);
    const [stores, setStores] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserData | any>(null);
    const [inviteMode, setInviteMode] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'delivery'>('all');
    const [verificationModal, setVerificationModal] = useState<{ isOpen: boolean; user: any | null }>({
        isOpen: false,
        user: null
    });

    const handleVerify = async (userId: string, status: string) => {
        try {
            await usersAPI.verify(userId, status);
            toast.success(`Agent ${status === 'verified' ? 'verified' : 'rejected'}`);
            setVerificationModal({ isOpen: false, user: null });
            fetchUsers();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Verification update failed');
        }
    };

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        role: 'staff',
        organizationId: '',
        storeId: ''
    });

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            setCurrentUser(user);
        }
        fetchUsers();
        fetchStores();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await usersAPI.getAll();
            setUsers(response.data);
        } catch (error: any) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            const response = await storesAPI.getAll();
            setStores(response.data);
        } catch (error) { }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingUser) {
                const { password, ...updateData } = formData;
                await usersAPI.update(editingUser._id, updateData);
                toast.success('User updated successfully');
            } else if (inviteMode) {
                const { password, ...inviteData } = formData;
                await usersAPI.invite(inviteData);
                toast.success('Invitation sent successfully');
            } else {
                await usersAPI.create(formData);
                toast.success('User created successfully');
            }
            setIsModalOpen(false);
            setEditingUser(null);
            setInviteMode(false);
            resetForm();
            fetchUsers();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const resetForm = () => {
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            password: '',
            role: 'staff',
            organizationId: currentUser?.organizationId || '',
            storeId: currentUser?.storeId || ''
        });
    };

    const handleEdit = (user: UserData) => {
        setEditingUser(user);
        setFormData({
            fullName: user.fullName,
            email: user.email,
            phone: user.phone || '',
            password: '',
            role: user.role,
            organizationId: typeof user.organizationId === 'object' ? user.organizationId?._id : user.organizationId || '',
            storeId: user.storeIds && user.storeIds.length > 0
                ? (typeof user.storeIds[0] === 'object' ? user.storeIds[0]?._id : user.storeIds[0])
                : ''
        });
        setIsModalOpen(true);
    };

    const toggleStatus = async (user: UserData) => {
        try {
            const newStatus = user.accountStatus === 'active' ? 'suspended' : 'active';
            await usersAPI.update(user._id, {
                accountStatus: newStatus,
                isActive: newStatus === 'active'
            });
            toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        if (activeTab === 'all') return matchesSearch;
        if (activeTab === 'pending') return matchesSearch && user.accountStatus === 'invited';
        return matchesSearch && user.role === 'delivery';
    });

    return (
        <Layout>
            <div className="space-y-8">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">User Management</h1>
                        <p className="text-gray-500 mt-1">Manage staff, admins and delivery agents across your organization</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => {
                                setEditingUser(null);
                                setInviteMode(false);
                                resetForm();
                                setIsModalOpen(true);
                            }}
                            className="flex items-center justify-center gap-2 bg-[#10b981] text-white px-6 py-3 rounded-2xl hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20 font-bold"
                        >
                            <Plus size={20} />
                            Add User
                        </button>
                        <button
                            onClick={() => {
                                setEditingUser(null);
                                setInviteMode(true);
                                resetForm();
                                setIsModalOpen(true);
                            }}
                            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 font-bold"
                        >
                            <Mail size={20} />
                            Invite
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm">
                    <div className="relative max-w-2xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                        />
                    </div>

                    <div className="flex items-center gap-8 mt-6 border-b border-gray-50">
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`pb-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'all' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            All Users
                            {activeTab === 'all' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#10b981] rounded-t-full" />}
                        </button>
                        <button
                            onClick={() => setActiveTab('pending')}
                            className={`pb-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'pending' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            Pending
                            <span className="ml-2 px-1.5 py-0.5 rounded-md text-[9px] bg-gray-100 text-gray-400">
                                {users.filter(u => u.accountStatus === 'invited').length}
                            </span>
                            {activeTab === 'pending' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full" />}
                        </button>
                        <button
                            onClick={() => setActiveTab('delivery')}
                            className={`pb-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === 'delivery' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            Delivery
                            {activeTab === 'delivery' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-t-full" />}
                        </button>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50/50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">User</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Role</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Store</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                <th className="px-6 py-5 text-right text-[10px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-24 text-center">
                                        <Loader2 className="animate-spin text-[#10b981] mx-auto mb-4" size={40} />
                                        <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading...</p>
                                    </td>
                                </tr>
                            ) : filteredUsers.map((user) => (
                                <tr key={user._id} className="hover:bg-gray-50/50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-green-50 text-[#10b981] rounded-2xl flex items-center justify-center font-black text-lg border border-green-100">
                                                {user.fullName.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900">{user.fullName}</p>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase">{user.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{user.role.replace('_', ' ')}</span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="text-xs font-bold text-gray-600">
                                            {user.storeIds && user.storeIds.length > 0
                                                ? (typeof user.storeIds[0] === 'object'
                                                    ? `${user.storeIds[0].name}${user.storeIds.length > 1 ? ` +${user.storeIds.length - 1}` : ''}`
                                                    : 'N/A')
                                                : 'Global'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <button
                                            onClick={() => toggleStatus(user)}
                                            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${user.accountStatus === 'active' ? 'bg-green-100 text-green-700' : user.accountStatus === 'invited' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}
                                        >
                                            {user.accountStatus}
                                        </button>
                                        {user.role === 'delivery' && (
                                            <button
                                                onClick={() => setVerificationModal({ isOpen: true, user })}
                                                className={`mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${user.verificationStatus === 'verified' ? 'bg-blue-50 text-blue-600' : 'bg-yellow-50 text-yellow-600'}`}
                                            >
                                                <Shield size={12} />
                                                {user.verificationStatus || 'PENDING'}
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => handleEdit(user)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl"><Edit2 size={18} /></button>
                                            <button onClick={() => { if (window.confirm('Delete user?')) usersAPI.delete(user._id).then(() => fetchUsers()); }} className="p-2 text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={18} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* User Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-xl font-bold">{editingUser ? 'Edit User' : inviteMode ? 'Invite User' : 'Add User'}</h2>
                                <button onClick={() => setIsModalOpen(false)}><X size={24} /></button>
                            </div>
                            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <input type="text" placeholder="Full Name" required value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl" />
                                    <input type="email" placeholder="Email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl" />
                                    <input type="tel" placeholder="Phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl" />
                                    {!editingUser && !inviteMode && (
                                        <input type="password" placeholder="Password" required value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl" />
                                    )}
                                    <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl">
                                        <option value="staff">Staff</option>
                                        <option value="entity_admin">Entity Admin</option>
                                        <option value="delivery">Delivery Agent</option>
                                        {currentUser?.role === 'super_user' && <option value="super_user">Super User</option>}
                                    </select>
                                    <select value={formData.storeId} onChange={e => setFormData({ ...formData, storeId: e.target.value })} disabled={!['super_user', 'entity_admin'].includes(currentUser?.role)} className="w-full px-4 py-2.5 bg-gray-50 border rounded-xl">
                                        <option value="">Global</option>
                                        {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div className="flex gap-4 pt-6">
                                    <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-gray-50 rounded-xl font-bold">Cancel</button>
                                    <button type="submit" className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold">{editingUser ? 'Update' : inviteMode ? 'Invite' : 'Create'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Verification Modal */}
                {verificationModal.isOpen && verificationModal.user && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl w-full max-w-2xl p-8 shadow-2xl relative">
                            <button onClick={() => setVerificationModal({ isOpen: false, user: null })} className="absolute right-6 top-6"><X size={24} /></button>
                            <h2 className="text-2xl font-black mb-2">Agent Verification</h2>
                            <p className="text-gray-500 mb-8 uppercase text-[10px] font-black">{verificationModal.user.fullName}</p>
                            <div className="space-y-6 mb-10">
                                {verificationModal.user.verificationDocuments?.map((doc: any, i: number) => (
                                    <div key={i} className="p-4 bg-gray-50 rounded-2xl flex items-center justify-between">
                                        <div>
                                            <p className="font-bold capitalize">{doc.type}</p>
                                            <a href={doc.url} target="_blank" className="text-[10px] text-blue-600 font-black">View Document</a>
                                        </div>
                                        <span className="px-2 py-1 rounded text-[8px] font-black uppercase bg-yellow-100 text-yellow-700">{doc.status}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => handleVerify(verificationModal.user._id, 'rejected')} className="flex-1 py-4 bg-red-50 text-red-600 rounded-2xl font-black">Reject</button>
                                <button onClick={() => handleVerify(verificationModal.user._id, 'verified')} className="flex-1 py-4 bg-[#10b981] text-white rounded-2xl font-black">Approve</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
